import { FilterMatchMode, FilterService } from "primereact/api";
import { Column, ColumnEvent } from "primereact/column";
import { PriorityBody } from "../components/Defect/PriorityBody";
import { PriorityFilter } from "../components/Defect/PriorityFilter";
import { Defect, DefectPriority, DefectStatus } from "../types/defect";
import { DefectTableActionsBody } from "../components/Defect/DefectTableActionsBody";
import { GradingAreaFilter } from "../components/Shared/GradingAreaFilter";
import {
    GENERIC_AREA,
    GradingAreaMapping,
    GradingArea
} from "../config/GradingAreaMapping";
import { GradingAreaBody } from "../components/Shared/GradingAreaBody";
import { PhotoFolderBody } from "../components/Shared/PhotoFolderBody";
import { DEFECT_ESTIMATED_COSTS } from "@idwal/idwal-utilities/lib/types/DefectEstimatedCosts";
import { InputTextareaEditor } from "../components/Shared/InputTextareaEditor";
import { DefectIdBody } from "../components/Defect/DefectIdBody";
import { PriorityEditor } from "../components/Defect/PriorityEditor";
import { EstimateCostEditorDropdown } from "../components/Defect/EstimateCostEditor";
import { EstimateCostBody } from "../components/Defect/EstimateCostBody";
import { EstimatedCostFilter } from "../components/Defect/EstimateCostFilter";
import { DefectTableImagesBody } from "../components/Defect/DefectTableImagesBody";
import { CustomEstimateCostBody } from "../components/Defect/CustomEstimateCostBody";
import { CustomEstimateCostEditor } from "../components/Defect/CustomEstimateCostEditor";
import { useFlags } from "launchdarkly-react-client-sdk";
import { StatusBody } from "../components/Shared/StatusBody";
import { StatusFilter } from "../components/Defect/StatusFilter";
import { DefectCodeBody } from "../components/Defect/DefectCodeBody";

export const defaultDefectFilters = {
    global: {
        value: null,
        matchMode: FilterMatchMode.CONTAINS
    },
    defectId: {
        value: null,
        matchMode: FilterMatchMode.CUSTOM
    },
    typeCode: {
        value: null,
        matchMode: FilterMatchMode.CONTAINS
    },
    priority: {
        value: null,
        matchMode: FilterMatchMode.IN
    },
    description: {
        value: null,
        matchMode: FilterMatchMode.CONTAINS
    },
    recommendations: {
        value: null,
        matchMode: FilterMatchMode.CONTAINS
    },
    status: {
        value: null,
        matchMode: FilterMatchMode.CONTAINS
    },
    gradingArea: {
        value: null,
        matchMode: FilterMatchMode.CUSTOM
    },
    estimateDefectCostRangeId: {
        value: null,
        matchMode: FilterMatchMode.IN
    }
};

export const defaultDefectColumns = (
    data: Defect[],
    onCellEditComplete: (event: ColumnEvent) => void,
    inspectionDisabled: boolean,
    enableLowPriorityDefects: boolean
) => {
    let columns = [
        <Column key="Delete" field="Delete" frozen selectionMode="multiple" />,
        <Column
            key="defectId"
            field="defectId"
            header="Defect Id"
            filter
            filterPlaceholder="Search by Id"
            sortable
            frozen
            className="small-column"
            body={DefectIdBody}
            showFilterMenu={false}
        />,
        <Column
            key="typeCode"
            field="typeCode"
            header="Defect Code"
            filter
            filterPlaceholder="Search by Code"
            sortable
            className="small-column"
            showFilterMenu={false}
            body={DefectCodeBody}
        />,
        <Column
            key="priority"
            field="priority"
            header="Priority"
            filter
            sortable
            body={PriorityBody}
            filterElement={PriorityFilter}
            editor={
                inspectionDisabled
                    ? undefined
                    : PriorityEditor(enableLowPriorityDefects)
            }
            onCellEditComplete={onCellEditComplete}
            showFilterMenu={false}
        />,
        <Column
            key="description"
            field="description"
            header="Description"
            className="large-column"
            editor={inspectionDisabled ? undefined : InputTextareaEditor}
            onCellEditComplete={onCellEditComplete}
        />,
        <Column
            key="recommendations"
            field="recommendations"
            header="Recommendations"
            className="large-column"
            editor={inspectionDisabled ? undefined : InputTextareaEditor}
            onCellEditComplete={onCellEditComplete}
        />
    ];

    if (inspectionDisabled) {
        columns.push(
            <Column
                key="status"
                field="status"
                header="Status"
                filter
                sortable
                body={StatusBody}
                filterElement={StatusFilter}
                showFilterMenu={false}
            />
        );
    }

    columns = columns.concat([
        <Column
            key="gradingArea"
            field="gradingArea"
            header="Grading Area"
            filter
            sortable
            className="small-column"
            body={GradingAreaBody}
            filterElement={GradingAreaFilter}
            showFilterMenu={false}
        />,
        <Column
            key="estimateDefectCostRangeId"
            field="estimateDefectCostRangeId"
            header="Estimated Costs"
            filter
            sortable
            className="small-column"
            body={EstimateCostBody}
            filterElement={EstimatedCostFilter}
            showFilterMenu={false}
            editor={inspectionDisabled ? undefined : EstimateCostEditorDropdown}
            onCellEditComplete={onCellEditComplete}
        />
    ]);

    if (data.some((defect) => defect.estimateDefectCostRangeId === 1)) {
        columns.push(
            <Column
                key="estimateDefectCost"
                field="estimateDefectCost"
                header="Custom Estimated"
                className="small-column"
                sortable
                body={CustomEstimateCostBody}
                editor={
                    inspectionDisabled ? undefined : CustomEstimateCostEditor
                }
                onCellEditComplete={onCellEditComplete}
            />
        );
    }

    columns = columns.concat([
        <Column
            key="photoFolder"
            field="photoFolder"
            header="Photo Folder"
            sortable
            className="small-column"
            body={PhotoFolderBody}
        />,
        <Column
            key="defectImages"
            header="Images"
            body={DefectTableImagesBody}
        />,
        <Column
            key="defectActions"
            header="Actions"
            body={DefectTableActionsBody}
        />
    ]);

    return columns;
};

export const getDefectEditableFields = (autogenerated: boolean) => {
    const fields = [
        "priority",
        "description",
        "recommendations",
        "gradingArea",
        "estimateDefectCostRangeId",
        "estimateDefectCost",
        "photoFolder"
    ];

    const { releaseDefectCodeEditing } = useFlags();

    if (releaseDefectCodeEditing || !autogenerated) {
        fields.push("typeCode");
    }

    return fields;
};

export const createDefaultDefect = (inspectionId: string) => {
    const defect: Defect = {
        defectId: 0,
        description: "",
        estimateDefectCost: null,
        estimateDefectCostRangeId: null,
        gradingArea: "",
        images: null,
        inspectionId,
        key: null,
        notes: null,
        photoFolder: null,
        priority: DefectPriority.Medium,
        rawAnswer: null,
        recommendations: null,
        shortGenName: null,
        status: DefectStatus.Open,
        typeCode: null,
        codeAssignReason: null
    };

    return defect;
};

FilterService.register("custom_defectId", (value, filter) => {
    if (!filter || filter.length === 0) {
        return true;
    }

    return value.toString().includes(filter.replaceAll("-", ""));
});

FilterService.register("custom_gradingArea", (value, filter) => {
    if (!filter || filter.length === 0) {
        return true;
    }

    const valuesArray = value.split(";");

    const matchedValues = valuesArray.filter((val: string) =>
        filter.includes(val)
    );

    return matchedValues.length > 0 ? matchedValues : null;
});

export const getGradingAreaFieldValue = (gradingArea: string) => {
    if (gradingArea == "") {
        return "";
    }

    if (gradingArea === GENERIC_AREA) {
        return GradingAreaMapping[GENERIC_AREA];
    }

    return gradingArea
        .split(";")
        .reduce((acc: string, curr: string, i: number): string => {
            if (curr !== GENERIC_AREA) {
                if (i !== 0) {
                    acc = acc + ", ";
                }

                acc =
                    acc + (GradingAreaMapping[curr as keyof GradingArea] || "");
            }

            return acc;
        }, "");
};

export const mapDefectEstimatedCosts = () =>
    DEFECT_ESTIMATED_COSTS.map((cost) => ({
        label: cost.label,
        value: cost.id
    }));
